import React, { FC } from 'react'
import styled from 'styled-components'
import { useHistory } from 'react-router-dom'
import { useApolloClient } from '@apollo/react-hooks'
import produce from 'immer'

// Constants
import { routes } from 'constants/routes'

// Hooks
import { useRemoveWorkspaceMember } from '../../operations'
import useUserProfile from 'modules/user/useUserProfile'

// Components
import { MemberCard } from '../../components/MemberCard'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { ModalInjectedProps } from 'containers/ModalManager'
import { useToast } from 'containers/ToastManager'

// Local Utils
import { getMemberEmail, getMemberName } from '../../utils'

// Types
import {
  WorkspaceMembershipFragment,
  GetWorkspacesDocument,
  GetWorkspacesQuery,
  GetWorkspacesQueryVariables,
} from 'gqlTypes'

// Styles
const StrongBreak = styled.strong`
  word-break: break-all;
`

const StyledMemberCard = styled(MemberCard)`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`
const Note = styled.p`
  padding-top: 20px;
`
interface MemberProps {
  member: WorkspaceMembershipFragment
  workspaceId: string
}

const MemberCopy: FC<Pick<MemberProps, 'member'>> = ({ member }) => {
  const memberName = getMemberName(member)

  const firstParagraph = member.isEditor ? (
    <>
      After you remove <StrongBreak>{memberName}</StrongBreak> they {"won't"} be
      able to view, comment or upload documents to this Workspace anymore.
    </>
  ) : (
    <>
      After you remove <StrongBreak>{memberName}</StrongBreak> they {"won't"} be
      able to view or comment on documents in this Workspace anymore.
    </>
  )

  return (
    <>
      <p>{firstParagraph}</p>
      {member.role !== 'GUEST' && (
        <p>
          <StrongBreak>{memberName}</StrongBreak> may still have private Drafts
          in this Workspace. Make sure they back up their work before you remove
          them.
        </p>
      )}
    </>
  )
}

type RemoveWorkspaceMemberModalProps = MemberProps & ModalInjectedProps

export const RemoveWorkspaceMemberModal: FC<RemoveWorkspaceMemberModalProps> = props => {
  const {
    member,
    hideModal,
    isModalOpen,
    workspaceId: currentWorkspaceId,
  } = props
  const { showToast } = useToast()
  const history = useHistory()
  const { data: profile } = useUserProfile()
  const { cache } = useApolloClient()

  const currentLoggedUserId = profile?.me.identifier
  const memberId = member?.user?.identifier
  const memberName = member.user?.name
  const memberEmail = getMemberEmail(member)

  const [removeMember, { loading }] = useRemoveWorkspaceMember({
    variables: { membershipId: member.identifier },
    onError: message => showToast(message, 'negative'),
    onCompleted: () => {
      if (currentLoggedUserId === memberId) {
        // The user is removing himself from the Workspace
        // First we remove current Workspace from cache
        const currentWorkspacesInState = cache.readQuery<
          GetWorkspacesQuery,
          GetWorkspacesQueryVariables
        >({
          query: GetWorkspacesDocument,
        })

        const newWorkspaces = produce(currentWorkspacesInState, draft => {
          if (currentWorkspacesInState?.me.workspaces) {
            const nextAvailableWorkspaces = currentWorkspacesInState?.me.workspaces.filter(
              workspace => workspace.identifier !== currentWorkspaceId
            )

            draft!.me.workspaces = nextAvailableWorkspaces
          }
        }) as GetWorkspacesQuery

        cache.writeQuery<GetWorkspacesQuery, GetWorkspacesQueryVariables>({
          query: GetWorkspacesDocument,
          data: newWorkspaces,
        })

        const currentWorkspaceName = currentWorkspacesInState?.me.workspaces.filter(
          workspace => workspace.identifier === currentWorkspaceId
        )[0].name

        // Then we redirect him to the entry point of the app
        showToast(`You left the "${currentWorkspaceName}" Workspace`)
        history.push(routes.ENTRY.create({}))
        hideModal()
        return
      }

      showToast(`${getMemberName(member)} was removed from this workspace`)
      hideModal()
    },
  })

  return (
    <ConfirmationDialog
      title="Remove this Member?"
      hideModal={hideModal}
      isModalOpen={isModalOpen}
      onConfirm={removeMember}
      confirmButton={{
        text: 'Remove Member',
        loading,
      }}>
      <>
        <MemberCopy member={member} />
        <MemberCard.BackgroundWrapper>
          <StyledMemberCard
            name={memberName}
            email={memberEmail!}
            avatarSrc={member?.user?.avatar?.small}
          />
        </MemberCard.BackgroundWrapper>
        {member.isEditor && (
          <Note>
            After removing this Member you can assign their role to somebody
            else
          </Note>
        )}
      </>
    </ConfirmationDialog>
  )
}
